import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { toast } from '@/hooks/use-toast';
import { toFriendlyLimitError } from '@/lib/limitErrors';
import { PerformanceMetricsSchema } from '../validators/content.validators';
import { contentSubmissionRepository } from '../repositories/contentSubmission.repository';
import { contentMetricsRepository } from '../repositories/contentMetrics.repository';
import { contentKeys } from '../services/content.keys';
import { deliverableKeys } from '../../deliverables/services/deliverable.keys';
import type {
  CreateSubmissionDto,
  UpdateSubmissionDto,
  ContentSubmission,
} from '../types/content.types';

// Helper to scrape metrics for newly created Instagram submissions
const fetchMetricsForRows = async (
  rows: ContentSubmission[],
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string
) => {
  const scrapable = rows.filter((row) => row.platform === 'instagram' && row.post_url);
  if (scrapable.length === 0) return;

  let succeeded = 0;
  let failed = 0;

  for (const row of scrapable) {
    try {
      const data = await contentMetricsRepository.triggerPostScrape(row.id);
      if (data?.error) {
        failed += 1;
      } else {
        succeeded += 1;
      }
    } catch {
      failed += 1;
    } finally {
      queryClient.invalidateQueries({ queryKey: ['deliverable-aggregates'] });
      queryClient.invalidateQueries({ queryKey: contentKeys.posts(orgId, row.campaign_influencer_id) });
      queryClient.invalidateQueries({ queryKey: contentKeys.legacyPosts(row.campaign_influencer_id) });
      queryClient.invalidateQueries({ queryKey: contentKeys.metricsHistory(orgId, row.id) });
      queryClient.invalidateQueries({ queryKey: contentKeys.legacyMetricsHistory(row.id) });
    }
  }

  toast({
    title: succeeded > 0 ? 'Metrics fetched' : 'Metrics fetch failed',
    description:
      failed > 0
        ? `Fetched metrics for ${succeeded} of ${scrapable.length} post${scrapable.length === 1 ? '' : 's'}. ${failed} failed and can be retried individually.`
        : `Fetched metrics for ${succeeded} post${succeeded === 1 ? '' : 's'}.`,
    variant: succeeded === 0 ? 'destructive' : 'default',
  });
};

export const useCreateContentSubmission = (explicitOrgId?: string) => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = explicitOrgId || currentOrganization?.id || '';

  return useMutation({
    mutationFn: async (data: CreateSubmissionDto) => {
      const activeOrgId = orgId || currentOrganization?.id;
      if (!activeOrgId) throw new Error('No organization selected');

      if (data.performance_metrics) {
        const validation = PerformanceMetricsSchema.safeParse(data.performance_metrics);
        if (!validation.success) {
          throw new Error(`Invalid metrics: ${validation.error.message}`);
        }
      }

      return contentSubmissionRepository.createSubmission(activeOrgId, data);
    },
    onSuccess: (data) => {
      const activeOrgId = orgId || currentOrganization?.id || '';
      queryClient.invalidateQueries({ queryKey: ['deliverable-aggregates'] });
      queryClient.invalidateQueries({ queryKey: contentKeys.posts(activeOrgId, data.campaign_influencer_id) });
      queryClient.invalidateQueries({ queryKey: contentKeys.legacyPosts(data.campaign_influencer_id) });
      toast({ title: 'Success', description: 'Post added successfully' });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: toFriendlyLimitError(error, error instanceof Error ? error.message : 'Failed to add post'),
        variant: 'destructive',
      });
    },
  });
};

export const useCreateContentSubmissionsBulk = (explicitOrgId?: string) => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const orgId = explicitOrgId || currentOrganization?.id || '';

  return useMutation({
    mutationFn: async (submissions: CreateSubmissionDto[]) => {
      const activeOrgId = orgId || currentOrganization?.id;
      if (!activeOrgId) throw new Error('No organization selected');
      if (submissions.length === 0) return [];

      submissions.forEach((data) => {
        if (data.performance_metrics) {
          const validation = PerformanceMetricsSchema.safeParse(data.performance_metrics);
          if (!validation.success) {
            throw new Error(`Invalid metrics: ${validation.error.message}`);
          }
        }
      });

      return contentSubmissionRepository.createSubmissionsBulk(activeOrgId, submissions);
    },
    onSuccess: (data) => {
      const orgId = currentOrganization?.id || '';
      queryClient.invalidateQueries({ queryKey: ['deliverable-aggregates'] });
      data.forEach((submission) => {
        queryClient.invalidateQueries({ queryKey: contentKeys.posts(orgId, submission.campaign_influencer_id) });
        queryClient.invalidateQueries({ queryKey: contentKeys.legacyPosts(submission.campaign_influencer_id) });
      });
      toast({
        title: 'Success',
        description: `Added ${data.length} post${data.length === 1 ? '' : 's'} successfully`,
      });
      if (orgId) {
        void fetchMetricsForRows(data, queryClient, orgId);
      }
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: toFriendlyLimitError(error, error instanceof Error ? error.message : 'Failed to add posts'),
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateContentSubmission = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async (params: UpdateSubmissionDto) => {
      if (!currentOrganization?.id) throw new Error('No organization selected');

      if (params.performance_metrics) {
        const validation = PerformanceMetricsSchema.safeParse(params.performance_metrics);
        if (!validation.success) {
          throw new Error(`Invalid metrics: ${validation.error.message}`);
        }
      }

      return contentSubmissionRepository.updateSubmission(currentOrganization.id, params);
    },
    onSuccess: (data) => {
      const orgId = currentOrganization?.id || '';
      queryClient.invalidateQueries({ queryKey: ['deliverable-aggregates'] });
      queryClient.invalidateQueries({ queryKey: contentKeys.posts(orgId, data.campaign_influencer_id) });
      queryClient.invalidateQueries({ queryKey: contentKeys.legacyPosts(data.campaign_influencer_id) });
      queryClient.invalidateQueries({ queryKey: contentKeys.metricsHistory(orgId, data.id) });
      queryClient.invalidateQueries({ queryKey: contentKeys.legacyMetricsHistory(data.id) });
      toast({ title: 'Success', description: 'Post updated successfully' });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update post',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteContentSubmission = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();

  return useMutation({
    mutationFn: async ({
      id,
      campaignInfluencerId,
    }: {
      id: string;
      campaignInfluencerId: string;
    }) => {
      if (!currentOrganization?.id) throw new Error('No organization selected');
      await contentSubmissionRepository.deleteSubmission(currentOrganization.id, id);
      return { id, campaignInfluencerId };
    },
    onSuccess: (data) => {
      const orgId = currentOrganization?.id || '';
      queryClient.invalidateQueries({ queryKey: ['deliverable-aggregates'] });
      queryClient.invalidateQueries({ queryKey: contentKeys.posts(orgId, data.campaignInfluencerId) });
      queryClient.invalidateQueries({ queryKey: contentKeys.legacyPosts(data.campaignInfluencerId) });
      toast({ title: 'Success', description: 'Post deleted successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete post', variant: 'destructive' });
    },
  });
};
